// Operatör denetim satırları tablosu: /audit sayfası ve müşteri detayının
// denetim sekmesi aynı GET /ops/v1/audit yanıtını aynı sütunlarla gösterir.

import { useTranslation } from "react-i18next";

import type { paths } from "@/api/types.gen";
import { TableScroll } from "@/components/TableScroll";
import { cn } from "@/lib/utils";

export type AuditEvent = NonNullable<
  paths["/ops/v1/audit"]["get"]["responses"][200]["content"]["application/json"]["data"]
>["events"][number];

export function AuditEventsTable({
  events,
}: {
  readonly events: readonly AuditEvent[];
}): React.ReactElement {
  const { t, i18n } = useTranslation();
  const empty = t("common.empty_value");
  const fmtAt = (iso: string): string =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "medium" }).format(
      new Date(iso),
    );

  return (
    <TableScroll>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-[var(--border-subtle)] bg-[var(--bg-subtle)]">
            {(["at", "operator", "action", "target", "reason"] as const).map((col) => (
              <th
                key={col}
                className="px-4 py-2.5 text-left font-sans text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--fg-muted)]"
              >
                {t(`audit.col.${col}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map((event, i) => (
            <tr
              key={event.event_id}
              className={cn(i > 0 && "border-t border-[var(--border-subtle)]")}
            >
              <td className="whitespace-nowrap px-4 py-3 font-mono text-[11.5px] text-[var(--fg-muted)]">
                {fmtAt(event.at)}
              </td>
              <td className="whitespace-nowrap px-4 py-3">
                {/* Ad üstte, id altta: denetim yüzeyi "kim" sorusunu okunur
                    cevaplamalı, ama id'yi de kaybetmemeli — korelasyon
                    (trace, ticket, başka sistem) id üzerinden yapılıyor. */}
                <div className="font-serif text-[13.5px] text-[var(--fg-primary)]">
                  {event.operator_name ?? empty}
                </div>
                <div className="font-mono text-[11px] text-[var(--fg-muted)]">
                  {event.operator_id}
                </div>
              </td>
              <td className="px-4 py-3">
                <span className="rounded-[5px] bg-[var(--bg-subtle)] px-2 py-0.5 font-mono text-[12px] text-[var(--fg-primary)]">
                  {event.action}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="font-mono text-[12px] text-[var(--fg-primary)]">
                  {event.target_id ?? empty}
                </div>
                <div className="font-mono text-[11px] text-[var(--fg-muted)]">
                  {event.target_kind ?? empty}
                  {event.target_tenant_id !== null && (
                    // Ayraç METİN, boşluklu bir span değil: eskiden burada
                    // yalnız görsel dolgu veren bir span vardı, yani ekran
                    // okuyucu ve metin çıkarımı iki alanı bitişik okuyordu
                    // ("tenant9ac13604…").
                    <> · {event.target_tenant_name ?? event.target_tenant_id}</>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 font-serif text-[13px] italic text-[var(--fg-muted)]">
                {event.reason ?? empty}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}
